import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * Selectors for third-party modals that are portaled to `<body>` (outside our
 * dialogs). When a dialog hosting one of these closes, the portaled overlay is
 * left orphaned on top of the page and swallows every click/hover. Each match is
 * dismissed via its own close control, or removed as a fallback.
 *
 * Extend this list as new portaled third-party modals are integrated.
 * @type {readonly string[]}
 */
const ORPHAN_OVERLAY_SELECTORS = ['.yotpo-qna-modal-backdrop'];

/**
 * Dismisses any orphaned third-party overlays left on the page. Safe to call on
 * every dialog close: it is a no-op when none are present.
 */
function dismissOrphanedOverlays() {
  for (const selector of ORPHAN_OVERLAY_SELECTORS) {
    for (const overlay of document.querySelectorAll(selector)) {
      const closeButton = overlay.querySelector('.btn-close, .close-btn-wrapper button, [aria-label="Close modal"]');

      if (closeButton instanceof HTMLElement) {
        closeButton.click();
      } else {
        overlay.remove();
      }
    }
  }
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    // Opt-in via the `teleport` attribute: relocate the dialog to <body> so its
    // fixed overlay isn't trapped by an ancestor's stacking / containing-block
    // context (e.g. a sticky column or flex/grid wrapper). The appendChild re-fires
    // connectedCallback with <body> as the parent, which then runs the real init.
    if (this.hasAttribute('teleport') && this.parentNode !== document.body) {
      // A theme-editor section re-render replaces the section markup but cannot reach
      // the copy already teleported out of it, so the stale twin would linger in
      // <body> and keep winning `getElementById`. Drop it before moving in.
      if (this.id) {
        for (const twin of document.querySelectorAll(`#${CSS.escape(this.id)}`)) {
          if (twin !== this) twin.remove();
        }
      }

      document.body.appendChild(this);
      return;
    }

    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /**
   * The pending `requestAnimationFrame` id for an in-flight open, or null.
   * Tracked so a close that arrives before the open frame runs can cancel it
   * instead of letting it lock the page after we've already closed.
   * @type {number | null}
   */
  #openAnimationFrame = null;

  /** Whether the body scroll-lock is currently applied. */
  #scrollLocked = false;

  /**
   * Locks body scroll, offsetting by the saved scroll position.
   * @param {number} scrollY
   */
  #lockScroll(scrollY) {
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    this.#scrollLocked = true;
  }

  /** Releases the body scroll-lock and restores the scroll position. Idempotent. */
  #unlockScroll() {
    if (!this.#scrollLocked) return;

    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    this.#scrollLocked = false;
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open || this.#openAnimationFrame != null) return;

    const scrollY = window.scrollY;
    this.#previousScrollY = scrollY;

    // Prevent layout thrashing by separating DOM reads from DOM writes
    this.#openAnimationFrame = requestAnimationFrame(() => {
      this.#openAnimationFrame = null;

      this.#lockScroll(scrollY);

      dialog.showModal();
      this.dispatchEvent(new DialogOpenEvent());

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    // A close arrived before the open frame ran (open→close race). Cancel the
    // pending open so it never locks the page, and bail out.
    if (this.#openAnimationFrame != null) {
      cancelAnimationFrame(this.#openAnimationFrame);
      this.#openAnimationFrame = null;
      this.#unlockScroll();
      return;
    }

    if (!dialog.open) {
      // Safety net: never leave the page scroll-locked even on a redundant close.
      this.#unlockScroll();
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    this.#unlockScroll();

    dialog.close();
    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());

    // A dialog can host a third-party widget that portals its own modal to
    // <body> (outside this dialog). Closing this dialog while that modal is open
    // would orphan its overlay and block the page — sweep any such overlays.
    dismissOrphanedOverlays();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName, { bubbles: true });
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;

        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);

/**
 * Custom dialog component that extends the base Component class.
 * This component provides dialog functionality with modal behavior.
 */
export class CustomDialogComponent extends Component {
  /** @type {HTMLElement | null} */
  #triggerElement = null;

  showModal() {
    this.#triggerElement = /** @type {HTMLElement | null} */ (document.activeElement);
    this.classList.add('_active');
    this.setAttribute('open', '');
    trapFocus(this);
    const focusTarget =
      this.querySelector('.modal__close') ??
      this.querySelector(
        'a[href], button:not([disabled]), input:not([type=hidden]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])'
      );
    if (focusTarget instanceof HTMLElement) focusTarget.focus();
  }

  close() {
    this.classList.remove('_active');
    this.removeAttribute('open');
    removeTrapFocus();
    this.#triggerElement?.focus?.();
    this.#triggerElement = null;
  }

  get open() {
    return this.hasAttribute('open');
  }
}

if (!customElements.get('custom-dialog-component')) customElements.define('custom-dialog-component', CustomDialogComponent);
